/**
 * TabSleepBanner — shown on the Active Alerts pages after the browser paused the page
 * in the background (see hooks/useTabSleepDetector.js). Explains why alert sounds may
 * have been missed and how to exempt this site from the browser's tab sleeping.
 *
 * Dismissable. It comes back on the next pause: until the site is exempted, every
 * pause is another stretch with no alert sounds, and that is worth saying each time.
 */

import { useState } from 'react';
import './tab-sleep-banner.css';

/* Which browser's settings to describe. userAgentData first (Chromium), then the UA
   string. Edge must be tested before Chrome: Edge's UA contains "Chrome" too. */
export function detectBrowser() {
  const brands = (typeof navigator !== 'undefined' && navigator.userAgentData?.brands) || [];
  if (brands.some((b) => /Microsoft Edge/i.test(b.brand))) return 'edge';
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  if (/Edg\//.test(ua)) return 'edge';
  if (/OPR\/|Opera/.test(ua)) return 'other';
  if (/Firefox\//.test(ua)) return 'firefox';
  if (/Chrome\//.test(ua)) return 'chrome';
  if (/Safari\//.test(ua)) return 'safari';
  return 'other';
}

const REASON_TEXT = {
  discarded: 'Your browser closed this page in the background to save memory, then reopened it when you came back.',
  frozen: 'Your browser paused this page while it was in the background.',
  paused: 'This page was paused while it was in the background, either by your browser or because the computer went to sleep.',
};

const HOW_TO = {
  edge: {
    steps: (host) => `To keep it running, open Edge Settings, go to System and performance, find "Never put these sites to sleep", choose Add, and enter ${host}.`,
    policy: 'SleepingTabsBlockedForUrls',
    browserName: 'Edge',
  },
  chrome: {
    steps: (host) => `To keep it running, open Chrome Settings, go to Performance, find "Always keep these sites active", choose Add, and enter ${host}.`,
    policy: 'TabDiscardExceptions',
    browserName: 'Chrome',
  },
  firefox: {
    steps: () => 'Firefox has no setting to keep a site awake. Keep this page open in its own window and avoid minimizing it.',
  },
  safari: {
    steps: () => 'Safari has no setting to keep a site awake. Keep this page open in its own window and avoid minimizing it.',
  },
  other: {
    steps: () => 'Check your browser\'s performance or memory saving settings for a list of sites to keep active, and add this one.',
  },
};

const TabSleepBanner = ({ wake, onDismiss }) => {
  const [copied, setCopied] = useState(false);
  if (!wake) return null;

  const host = typeof window !== 'undefined' ? window.location.host : '';
  const howTo = HOW_TO[detectBrowser()] || HOW_TO.other;

  const copyHost = async () => {
    try {
      await navigator.clipboard.writeText(host);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked (insecure context or permission); the address is on screen */
    }
  };

  return (
    <div className="tsb-banner" role="status" aria-live="polite">
      <span className="tsb-banner__icon" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      </span>

      <div className="tsb-banner__body">
        <span className="tsb-banner__title">Alert sounds may have been missed</span>
        <span className="tsb-banner__detail">
          {REASON_TEXT[wake.reason] || REASON_TEXT.paused}
          {' '}While it was paused, new alerts could not arrive or play a sound. The alert list has been refreshed, and if any alerts came in, one tone plays (unless sound is muted).
        </span>
        <span className="tsb-banner__detail">
          {howTo.steps(host)}
          {howTo.policy ? (
            <> Managing many computers? Your IT team can apply this to all of them with the {howTo.browserName} policy <code className="tsb-banner__code">{howTo.policy}</code>.</>
          ) : null}
        </span>
      </div>

      <div className="tsb-banner__actions">
        {howTo.policy && host ? (
          <button type="button" className="tsb-banner__btn" onClick={copyHost}>
            {copied ? 'Copied' : 'Copy site address'}
          </button>
        ) : null}
        <button type="button" className="tsb-banner__close" onClick={onDismiss} aria-label="Dismiss">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default TabSleepBanner;
